"use client";

import { useState } from "react";
import { ATTR_FILTER_OPTIONS, NUMERIC_FILTER_OPTIONS } from "@/lib/constants";
import type { NumFilter } from "@/lib/filtering";

interface Props {
  attrValues: Record<string, string[]>;
  attrFilters: Record<string, string[]>;
  numFilters: NumFilter[];
  onToggleAttrValue: (key: string, val: string) => void;
  onSetNumFilter: (key: string, min: number | null, max: number | null) => void;
  onClearNumFilter: (key: string) => void;
  exitState?: "open" | "closing";
}

export default function FilterPanel({
  attrValues, attrFilters, numFilters,
  onToggleAttrValue, onSetNumFilter, onClearNumFilter,
  exitState = "open",
}: Props) {
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);

  return (
    <div
      className="ui-popover-panel ui-popover-panel-left ui-dropdown-panel absolute top-full left-0 mt-1 w-[280px] z-50 max-h-[360px] overflow-y-auto"
      data-state={exitState}
    >

      {/* Attr filters */}
      <div className="px-2.5 py-1.5 border-b rounded-t-lg" style={{ background: "var(--row-odd-bg)", borderColor: "var(--table-border)" }}>
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--table-header-text)" }}>Характеристики</span>
      </div>
      {ATTR_FILTER_OPTIONS.map(opt => {
        const selected = attrFilters[opt.value] ?? [];
        const vals = attrValues[opt.value] ?? [];
        const isOpen = expandedFilter === opt.value;
        return (
          <div key={opt.value} className="border-b" style={{ borderColor: "var(--table-border-light)" }}>
            <button
              onClick={() => setExpandedFilter(isOpen ? null : opt.value)}
              className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs transition-colors"
              style={{ color: selected.length > 0 ? "var(--chip-color)" : "var(--text-secondary)" }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = "var(--dropdown-hover)"}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ""}
            >
              <span style={{ fontWeight: selected.length > 0 ? 500 : undefined }}>{opt.label}</span>
              <div className="flex items-center gap-1.5">
                {selected.length > 0 && (
                  <span className="rounded-full px-1.5 text-[10px] leading-4" style={{ background: "var(--chip-color)", color: "var(--chip-bg)" }}>{selected.length}</span>
                )}
                <span className="text-[10px]" style={{ color: "var(--table-header-text)" }}>{isOpen ? "▴" : "▾"}</span>
              </div>
            </button>
            {isOpen && (
              <div className="px-2.5 pb-2 max-h-28 overflow-y-auto">
                {vals.length === 0 && <p className="text-xs py-1" style={{ color: "var(--table-header-text)" }}>Нет данных</p>}
                <div className="flex flex-wrap gap-1 pt-1">
                  {vals.map(v => (
                    <button
                      key={v}
                      onClick={() => onToggleAttrValue(opt.value, v)}
                      className="px-1.5 py-0.5 text-[11px] rounded-full border border-transparent transition-colors"
                      style={selected.includes(v) ? { background: "var(--chip-bg)", color: "var(--chip-color)" } : { background: "var(--surface-unselected)", color: "var(--text-secondary)" }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Numeric filters */}
      <div className="px-2.5 py-1.5 border-b" style={{ background: "var(--row-odd-bg)", borderColor: "var(--table-border)" }}>
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--table-header-text)" }}>Числовые</span>
      </div>
      {NUMERIC_FILTER_OPTIONS.map(opt => {
        const existing = numFilters.find(f => f.key === opt.value);
        const isOpen = expandedFilter === `num_${opt.value}`;
        return (
          <div key={opt.value} className="border-b" style={{ borderColor: "var(--table-border-light)" }}>
            <button
              onClick={() => setExpandedFilter(isOpen ? null : `num_${opt.value}`)}
              className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs transition-colors"
              style={{ color: existing ? "var(--chip-color)" : "var(--text-secondary)", fontWeight: existing ? 500 : undefined }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = "var(--dropdown-hover)"}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ""}
            >
              <span>{opt.label}</span>
              <div className="flex items-center gap-1.5">
                {existing && <span className="w-2 h-2 rounded-full" style={{ background: "var(--chip-color)" }} />}
                <span className="text-[10px]" style={{ color: "var(--table-header-text)" }}>{isOpen ? "▴" : "▾"}</span>
              </div>
            </button>
            {isOpen && (
              <div className="px-2.5 pb-2 flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-[10px] block mb-0.5" style={{ color: "var(--table-header-text)" }}>от</label>
                  <input
                    type="number"
                    defaultValue={existing?.min ?? ""}
                    placeholder="—"
                    className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    style={{ borderColor: "var(--input-border)", background: "var(--dropdown-hover)", color: "var(--table-text)" }}
                    onChange={e => {
                      const min = e.target.value !== "" ? parseFloat(e.target.value) : null;
                      const max = existing?.max ?? null;
                      if (min === null && max === null) onClearNumFilter(opt.value);
                      else onSetNumFilter(opt.value, min, max);
                    }}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] block mb-0.5" style={{ color: "var(--table-header-text)" }}>до</label>
                  <input
                    type="number"
                    defaultValue={existing?.max ?? ""}
                    placeholder="—"
                    className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    style={{ borderColor: "var(--input-border)", background: "var(--dropdown-hover)", color: "var(--table-text)" }}
                    onChange={e => {
                      const max = e.target.value !== "" ? parseFloat(e.target.value) : null;
                      const min = existing?.min ?? null;
                      if (min === null && max === null) onClearNumFilter(opt.value);
                      else onSetNumFilter(opt.value, min, max);
                    }}
                  />
                </div>
                {existing && (
                  <button onClick={() => onClearNumFilter(opt.value)} className="mt-4 text-sm hover:text-red-500" style={{ color: "var(--table-header-text)" }}>×</button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
